import { Clock, Select } from "@ews-admin/global-design-system";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { pinApi } from "../../../services/pin.api";
import {
  selectPinLockState,
  setPinLockMinutes,
} from "../../../store/reducers/sessionSlice";
import type { RootState } from "../../../store/store";
import {
  DEFAULT_PIN_LOCK_MINUTES,
  PIN_LOCK_MINUTES_OPTIONS,
} from "../../../utils/pin-utils";

interface PinLockDelaySectionProps {
  onChanged: () => void;
}

/** User settings: minutes without activity before the screen locks (MED-1643). */
export const PinLockDelaySection: React.FC<PinLockDelaySectionProps> = ({
  onChanged,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { hasPin } = useSelector(selectPinLockState);
  const pinLockMinutes = useSelector(
    (state: RootState) => state.session.session?.pinLockMinutes
  );
  const [saving, setSaving] = useState(false);

  // No PIN yet, or an API without the PIN lock: the screen never locks.
  if (hasPin !== true) return null;

  const onChange = async (value: unknown) => {
    const lockMinutes = Number(value);
    if (lockMinutes === (pinLockMinutes ?? DEFAULT_PIN_LOCK_MINUTES)) return;
    setSaving(true);
    try {
      const response = await pinApi.updateLockDelay(lockMinutes);
      dispatch(setPinLockMinutes(response.lockMinutes));
      onChanged();
    } catch (error) {
      // Error is automatically dispatched by API interceptor
      console.error("Lock delay update failed:", error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="pt-6 mt-8 space-y-4 border-t border-neutral-200"
      data-testid="pin-lock-delay-section"
    >
      <div className="flex items-center space-x-3">
        <div className="flex justify-center items-center w-8 h-8 rounded-lg bg-ews-primary-100">
          <Clock className="w-4 h-4 text-primary-600" />
        </div>
        <h3 className="text-lg font-semibold text-neutral-900">
          {t("userSettings.pinLockDelay.title")}
        </h3>
      </div>
      <p className="text-sm text-neutral-600">
        {t("userSettings.pinLockDelay.description")}
      </p>
      <div className="max-w-xs">
        <Select
          label={t("userSettings.pinLockDelay.label")}
          value={pinLockMinutes ?? DEFAULT_PIN_LOCK_MINUTES}
          onChange={onChange}
          disabled={saving}
          options={PIN_LOCK_MINUTES_OPTIONS.map((minutes) => ({
            value: minutes,
            label: t("userSettings.pinLockDelay.minutes", { count: minutes }),
          }))}
        />
      </div>
    </div>
  );
};
